import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Layers, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { projectsData } from "../data/data";

const categories = ["All", "Residential", "Industrial", "Institutional"];

const categoryColors = {
  Residential: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Industrial: "bg-amber-50 text-amber-700 border-amber-200",
  Institutional: "bg-sky-50 text-sky-700 border-sky-200",
};

// Page Header
const PageHeader = () => (
  <section className="bg-pc-primary pt-32 pb-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-[2px] bg-pc-accent" />
        <span className="text-pc-accent font-heading text-sm font-semibold uppercase tracking-[0.15em]">
          Our Work
        </span>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl lg:text-[52px] font-extrabold text-white leading-[1.1] tracking-tight mb-5">
        Projects
      </h1>
      <p className="font-body text-white/50 text-lg max-w-2xl leading-relaxed">
        A selection of projects we have completed across residential, industrial,
        and institutional sectors in Gujarat.
      </p>
    </motion.div>
  </section>
);

export default function Projects() {
  const [activeCategory, setActiveCategory] = useState("All");
  const projects = activeCategory === "All" 
    ? projectsData 
    : projectsData.filter(p => p.category === activeCategory);

  return (
    <main>
      <PageHeader />

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-14">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-lg text-sm font-heading font-semibold transition-colors duration-200 ${activeCategory === cat
                  ? "bg-pc-primary text-white shadow-md"
                  : "bg-pc-light text-pc-muted hover:text-pc-primary hover:bg-pc-primary/5"
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card
                  className="border-pc-border/60 hover:shadow-xl card-accent-hover transition-all duration-300 rounded-xl h-full flex flex-col"
                >
                  <CardContent className="p-7">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <h3 className="font-heading text-lg font-bold text-pc-primary">
                        {project.title}
                      </h3>
                      <Badge
                        className={`text-[11px] font-medium border flex-shrink-0 rounded-md ${categoryColors[project.category] || "bg-pc-light text-pc-muted"
                          }`}
                      >
                        {project.category}
                      </Badge>
                    </div>

                    <p className="font-body text-sm text-pc-muted leading-relaxed mb-5">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-4 text-xs font-body text-pc-muted">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-pc-accent/60" />
                        {project.location}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-pc-accent/60" />
                        {project.year}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-pc-accent/60" />
                        {project.scope}
                      </div>
                    </div>
                  </CardContent>
                  {project.link && (
                    <div className="px-7 pb-7 pt-0 mt-auto">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm font-semibold text-pc-accent hover:text-pc-accent/80 transition-colors"
                      >
                        Visit
                        <ArrowRight className="w-4 h-4 ml-1.5" />
                      </a>
                    </div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>

          {projects.length === 0 && (
            <div className="text-center py-16">
              <p className="font-body text-pc-muted text-base">
                No projects found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-pc-light py-20">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-pc-primary tracking-tight mb-4">
            Have a Similar Project?
          </h2>
          <p className="font-body text-pc-muted text-base max-w-lg mx-auto mb-8">
            We'd be happy to discuss your requirements and share how we can help.
          </p>
          <Link to="/contact">
            <Button className="bg-pc-accent hover:bg-pc-accent/90 text-white text-sm font-heading font-semibold px-8 h-12 rounded-lg transition-colors duration-200">
              Discuss Your Project
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </section>
    </main>
  );
}
